/**
 * Limite de tamanho de VÍDEO por canal da Meta, e a validação que roda antes do
 * upload.
 *
 * ## Por que existe
 *
 * O envio de mídia para canais Meta é por LINK: o whiz-server manda
 * `{ video: { link } }` e é a **Meta** quem baixa o arquivo do nosso storage. O
 * servidor nunca toca nos bytes — de propósito. Consequência: um arquivo grande
 * demais sobe inteiro, o `POST /messages` responde `200` com um `wamid`, e a
 * recusa só chega segundos depois, por webhook:
 *
 * ```
 * 131053 Media upload error — Video file has size 63787247 bytes but must be
 * atmost 16777216 bytes and non-empty
 * ```
 *
 * Do ponto de vista do operador isso é um vídeo que "sumiu": o upload mostrou
 * 100%, e a falha aparece depois, sem explicação acionável. Validar aqui, antes
 * do upload, troca esses 30 segundos de limbo por uma frase na hora.
 *
 * ## O que este módulo NÃO faz
 *
 * Não converte e não comprime — isso é a feature seguinte, por WebCodecs (com
 * fallback por core ffmpeg.wasm de vídeo, build próprio). O core self-hospedado
 * hoje em `public/ffmpeg/` é um build **só-áudio**
 * (`--disable-everything --enable-encoder=aac`): não decodifica nem codifica
 * vídeo, então não serve.
 */

/** Canais que falam com a Meta e, portanto, têm teto de payload. */
export type PlataformaMeta =
  | 'WHATSAPP_META'
  | 'INSTAGRAM_META'
  | 'INSTAGRAM_LOGIN'
  | 'MESSENGER_META';

const MB = 1024 * 1024;

/**
 * Teto de vídeo por canal, em bytes.
 *
 * `WHATSAPP_META` é 16 MiB — confirmado pela própria Meta na recusa
 * (`must be atmost 16777216 bytes`), não por documentação. As superfícies
 * sociais aceitam 25 MB.
 */
export const LIMITE_VIDEO_POR_PLATAFORMA: Readonly<Record<PlataformaMeta, number>> = {
  WHATSAPP_META: 16 * MB,
  INSTAGRAM_META: 25 * MB,
  INSTAGRAM_LOGIN: 25 * MB,
  MESSENGER_META: 25 * MB,
};

/** Rótulo do canal para a mensagem ao operador. */
const NOME_DO_CANAL: Readonly<Record<PlataformaMeta, string>> = {
  WHATSAPP_META: 'WhatsApp oficial',
  INSTAGRAM_META: 'Instagram',
  INSTAGRAM_LOGIN: 'Instagram',
  MESSENGER_META: 'Messenger',
};

/**
 * Containers que a Cloud API aceita em vídeo. Fora destes a Meta recusa mesmo
 * cabendo no tamanho — `video/webm` é o caso comum, porque é o que o navegador
 * grava e o que muita ferramenta de tela exporta.
 */
const CONTAINERS_ACEITOS = ['video/mp4', 'video/3gpp', 'video/3gp'];

export function ehPlataformaMeta(
  plataforma?: string | null,
): plataforma is PlataformaMeta {
  return (
    plataforma === 'WHATSAPP_META' ||
    plataforma === 'INSTAGRAM_META' ||
    plataforma === 'INSTAGRAM_LOGIN' ||
    plataforma === 'MESSENGER_META'
  );
}

/** Teto do canal, ou `null` quando o canal não fala com a Meta. */
export function limiteDeVideo(plataforma?: string | null): number | null {
  return ehPlataformaMeta(plataforma)
    ? LIMITE_VIDEO_POR_PLATAFORMA[plataforma]
    : null;
}

export function containerDeVideoAceito(mime?: string | null): boolean {
  if (!mime) return true; // sem mime declarado não há o que afirmar
  const base = mime.split(';').at(0)?.trim().toLowerCase() ?? '';
  return CONTAINERS_ACEITOS.includes(base);
}

/** `63787247` → `"60,8 MB"`. */
export function formatarBytes(bytes: number): string {
  if (bytes < MB) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / MB).toFixed(1).replace('.', ',')} MB`;
}

export type ProblemaDeVideo =
  | { tipo: 'ok' }
  | { tipo: 'grande-demais'; mensagem: string; limite: number; tamanho: number }
  | { tipo: 'container-recusado'; mensagem: string };

/**
 * Decide se este vídeo pode ir para este canal.
 *
 * Só opina sobre canais Meta: Evolution/wa-go têm outro transporte e outros
 * limites, e um `ok` aqui para eles é a resposta certa.
 */
export function validarVideoParaMeta(entrada: {
  plataforma?: string | null;
  tamanhoBytes?: number | null;
  mime?: string | null;
}): ProblemaDeVideo {
  const { plataforma, tamanhoBytes, mime } = entrada;

  const limite = limiteDeVideo(plataforma);
  if (limite === null) return { tipo: 'ok' };

  const canal = NOME_DO_CANAL[plataforma as PlataformaMeta];

  if (typeof tamanhoBytes === 'number' && tamanhoBytes > limite) {
    return {
      tipo: 'grande-demais',
      limite,
      tamanho: tamanhoBytes,
      mensagem:
        `Este vídeo tem ${formatarBytes(tamanhoBytes)} e o limite do ${canal} é ` +
        `${formatarBytes(limite)}. Comprima o vídeo (ou envie um trecho) antes de mandar — ` +
        `a Meta recusa o arquivo depois do envio, e a mensagem não chega.`,
    };
  }

  if (!containerDeVideoAceito(mime)) {
    return {
      tipo: 'container-recusado',
      mensagem:
        `O ${canal} só aceita vídeo em MP4 (H.264 + AAC), e este arquivo é ` +
        `${mime ?? 'de formato desconhecido'}. Converta para MP4 antes de enviar.`,
    };
  }

  return { tipo: 'ok' };
}
